import { useState, useEffect } from "react";
import { Star, Clock, Thermometer, Package, Truck, MapPin, DollarSign, Calendar, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import StarRating from "./StarRating";
import KudozDisplay from "./KudozDisplay";

// Mock order data for frontend demo
export interface OrderData {
  id: string;
  restaurantId: string;
  restaurantName: string;
  type: 'takeout' | 'delivery';
  status: 'delivered' | 'ready';
  items: {
    itemId: string;
    itemName: string;
    quantity: number;
    price: number;
    specialInstructions?: string;
  }[];
  subtotal: number;
  tax: number;
  tip: number;
  total: number;
  deliveryAddress?: string;
  pickupTime?: string;
  deliveryTime?: string;
  estimatedDeliveryTime?: string;
  actualDeliveryTime?: string;
  createdAt: string;
}

interface OrderSpecificRating {
  category: string;
  rating: number;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

interface PostOrderReviewPromptProps {
  isOpen: boolean;
  onClose: () => void;
  orderData: OrderData;
  onReviewSubmitted?: (reviewData: any) => void;
  onSkip?: () => void;
}

export default function PostOrderReviewPrompt({
  isOpen,
  onClose,
  orderData,
  onReviewSubmitted,
  onSkip
}: PostOrderReviewPromptProps) {
  const [overallRating, setOverallRating] = useState(0);
  const [orderSpecificRatings, setOrderSpecificRatings] = useState<OrderSpecificRating[]>([]);
  const [reviewText, setReviewText] = useState("");
  const [kudozEarned, setKudozEarned] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Initialize order-specific rating categories based on order type
  useEffect(() => {
    const baseCategories: OrderSpecificRating[] = [
      {
        category: 'food_quality',
        rating: 0,
        icon: Star,
        description: 'How was the food quality?'
      },
      {
        category: 'food_temperature',
        rating: 0,
        icon: Thermometer,
        description: 'Was the food at the right temperature?'
      },
      {
        category: 'packaging',
        rating: 0,
        icon: Package,
        description: 'How was the packaging quality?'
      }
    ];

    if (orderData.type === 'delivery') {
      baseCategories.push(
        {
          category: 'delivery_speed',
          rating: 0,
          icon: Clock,
          description: 'How was the delivery speed?'
        },
        {
          category: 'delivery_service',
          rating: 0,
          icon: Truck,
          description: 'How was the delivery service?'
        }
      );
    } else {
      baseCategories.push({
        category: 'pickup_experience',
        rating: 0,
        icon: Clock,
        description: 'How was the pickup experience?'
      });
    }

    setOrderSpecificRatings(baseCategories);
  }, [orderData.type]);

  // Calculate Kudoz based on PDF specification
  useEffect(() => {
    let total = 0;
    
    // 1. Base review: +10 Kudoz (awarded for submitting any review)
    total += 10;
    
    // 2. Vibe rating (overall rating): +5 Kudoz
    if (overallRating > 0) {
      total += 5;
    }
    
    // 3. Item/category ratings: +5 Kudoz per category rated
    const ratedCategories = orderSpecificRatings.filter(cat => cat.rating > 0);
    total += ratedCategories.length * 5;

    setKudozEarned(total);
  }, [overallRating, reviewText, orderSpecificRatings]);

  const updateCategoryRating = (category: string, rating: number) => {
    setOrderSpecificRatings(prev =>
      prev.map(cat =>
        cat.category === category ? { ...cat, rating } : cat
      )
    );
  };

  const formatPrice = (amount: number) => `$${amount.toFixed(2)}`;

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDeliveryTimeDifference = () => {
    if (!orderData.estimatedDeliveryTime || !orderData.actualDeliveryTime) return null;
    
    const estimated = new Date(orderData.estimatedDeliveryTime).getTime();
    const actual = new Date(orderData.actualDeliveryTime).getTime();
    const diffMinutes = Math.round((actual - estimated) / (1000 * 60));
    
    if (diffMinutes <= 0) return "On time or early";
    return `${diffMinutes} min late`;
  };

  const handleSubmit = () => {
    if (overallRating === 0) {
      toast({
        title: "Rating Required",
        description: "Please provide an overall rating for your order.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    const reviewData = {
      orderId: orderData.id,
      restaurantId: orderData.restaurantId,
      restaurantName: orderData.restaurantName,
      overallRating,
      reviewText: reviewText || null,
      orderSpecificRatings: orderSpecificRatings.filter(cat => cat.rating > 0),
      orderType: orderData.type,
      kudozEarned,
      isPostOrderReview: true,
      orderItems: orderData.items,
      orderTotal: orderData.total
    };

    // Mock API submission
    console.log('Post-order review submitted:', reviewData);

    setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: "Review Submitted!",
        description: `Thank you! You earned ${kudozEarned} Kudoz for your detailed order review.`,
      });
      
      onReviewSubmitted?.(reviewData);
      onClose();
    }, 1500);
  };

  const handleSkip = () => {
    console.log('Post-order review skipped for order:', orderData.id);
    onSkip?.();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90svh] overflow-y-auto" data-testid="post-order-review-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>How was your order?</span>
            <KudozDisplay amount={kudozEarned} size="sm" variant="badge" animated />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Summary */}
          <Card className="border-l-4 border-l-primary">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5" />
                {orderData.restaurantName}
                <Badge variant={orderData.type === 'delivery' ? 'default' : 'secondary'}>
                  {orderData.type === 'delivery' ? 'Delivery' : 'Pickup'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Order Items */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Items Ordered</Label>
                <div className="space-y-1">
                  {orderData.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-start text-sm">
                      <div className="flex-1">
                        <span className="font-medium">{item.quantity}x {item.itemName}</span>
                        {item.specialInstructions && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Note: {item.specialInstructions}
                          </div>
                        )}
                      </div>
                      <span className="font-medium">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Order Timing */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  <Label className="text-xs">Ordered</Label>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatTime(orderData.createdAt)}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    {orderData.type === 'delivery' ? 'Delivered' : 'Ready'}
                  </Label>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTime(orderData.deliveryTime || orderData.pickupTime || orderData.createdAt)}
                  </div>
                </div>
              </div>

              {/* Delivery Performance */}
              {orderData.type === 'delivery' && getDeliveryTimeDifference() && (
                <div className="text-sm">
                  <Label className="text-xs">Delivery Performance</Label>
                  <div className="text-muted-foreground">{getDeliveryTimeDifference()}</div>
                </div>
              )}

              {/* Order Total */}
              <Separator />
              <div className="flex justify-between items-center font-medium">
                <span>Total</span>
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {formatPrice(orderData.total)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Overall Rating */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Overall Experience</Label>
            <div className="flex items-center gap-3">
              <StarRating
                rating={overallRating}
                onChange={setOverallRating}
                size="lg"
                data-testid="overall-rating"
              />
              {overallRating > 0 && (
                <span className="text-sm text-muted-foreground">
                  {overallRating === 5 ? 'Excellent!' : 
                   overallRating === 4 ? 'Very Good' :
                   overallRating === 3 ? 'Good' :
                   overallRating === 2 ? 'Fair' : 'Needs Improvement'}
                </span>
              )}
            </div>
          </div>

          {/* Order-Specific Ratings */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Rate Your Experience</Label>
            <div className="grid gap-3">
              {orderSpecificRatings.map((category) => {
                const IconComponent = category.icon;
                return (
                  <Card key={category.category} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-muted rounded-lg">
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium">{category.description}</span>
                      </div>
                      <StarRating
                        rating={category.rating}
                        onChange={(rating) => updateCategoryRating(category.category, rating)}
                        size="sm"
                        data-testid={`rating-${category.category}`}
                      />
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Review Text */}
          <div className="space-y-2">
            <Label htmlFor="review-text" className="text-base font-medium">
              Share More Details (Optional)
            </Label>
            <textarea
              id="review-text"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Tell others about your experience... (optional)"
              className="w-full min-h-20 p-3 border rounded-md resize-none text-sm"
              maxLength={300}
              data-testid="review-text"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{reviewText.length}/300 characters</span>
            </div>
          </div>

          {/* Kudoz Breakdown */}
          {kudozEarned > 0 && (
            <Card className="bg-kudoz/5 border-kudoz/20">
              <CardContent className="pt-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base review:</span>
                    <span className="text-kudoz font-medium">+10 Kudoz</span>
                  </div>
                  {overallRating > 0 && (
                    <div className="flex justify-between">
                      <span>Vibe rating:</span>
                      <span className="text-kudoz font-medium">+5 Kudoz</span>
                    </div>
                  )}
                  {orderSpecificRatings.filter(cat => cat.rating > 0).length > 0 && (
                    <div className="flex justify-between">
                      <span>Category ratings ({orderSpecificRatings.filter(cat => cat.rating > 0).length}):</span>
                      <span className="text-kudoz font-medium">+{orderSpecificRatings.filter(cat => cat.rating > 0).length * 5} Kudoz</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-medium">
                    <span>Total Kudoz:</span>
                    <span className="text-kudoz">+{kudozEarned} Kudoz</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1"
              disabled={isSubmitting}
              data-testid="button-skip-review"
            >
              Skip for Now
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={overallRating === 0 || isSubmitting}
              className="flex-1"
              data-testid="button-submit-review"
            >
              {isSubmitting ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Review
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>

          {/* Skip Notice */}
          <div className="text-xs text-muted-foreground text-center">
            You can always review this order later from your order history
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}